import { Alert, CircularProgress } from "@mui/material";
import {
  type ActiveElement,
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  type ChartEvent,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import * as React from "react";

import {
  AggregateMetricsRequest,
  ChartDisplayType,
  NumericAggregation,
  Period,
} from "@/shared/domain/metrics";
import { useAggregateMetrics } from "@/shared/domain/metrics/hooks";

import { fillMissingBuckets } from "./fillMissingBuckets";
import styles from "./MetricChart.module.css";
import { nsToDateLabel } from "./nsConversions";

// Register only the Chart.js pieces this chart uses (matches the explicit
// registration style in PlotRenderer.ts). Registration is global + idempotent.
Chart.register(
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
);

// Canvas can't resolve CSS custom properties, so read the concrete color off
// the element at draw time, with a fallback matching --primary-main.
const FALLBACK_COLOR = "rgb(2, 136, 209)";

export interface MetricChartProps {
  metricName: string;
  orgId: string | undefined;
  period: Period;
  aggregation: NumericAggregation;
  /** Inclusive lower bound, Unix-epoch nanoseconds. */
  startTimeNs: number;
  /** Exclusive upper bound, Unix-epoch nanoseconds. */
  endTimeNs: number;
  /** Optional: restrict to specific device(s). */
  deviceIds?: string[];
  /** Optional: restrict to specific session(s). */
  sessionIds?: string[];
  /** Line (default) or bar rendering. */
  display?: ChartDisplayType;
  /** Fired when a real (non-placeholder) bucket is clicked. */
  onBucketClick?: (bucket: { start_time: number; end_time: number }) => void;
}

interface PlotDatum {
  /** Bucket start, Unix-epoch nanoseconds. */
  start_time: number;
  /** Bucket end, Unix-epoch nanoseconds. */
  end_time: number;
  /** Absent on placeholder rows; present on real aggregate buckets. */
  value?: number;
  /** Pre-formatted x-axis label (short ISO date). */
  label: string;
}

export function MetricChart({
  metricName,
  orgId,
  period,
  aggregation,
  startTimeNs,
  endTimeNs,
  deviceIds,
  sessionIds,
  display = "line",
  onBucketClick,
}: MetricChartProps) {
  const request: AggregateMetricsRequest = {
    name: metricName,
    period,
    aggregation,
    time_filter: "end_time",
    start_time_ns: startTimeNs,
    end_time_ns: endTimeNs,
    include_device_ids: deviceIds,
    include_session_ids: sessionIds,
  };

  const { data, isLoading, error } = useAggregateMetrics(request, orgId);

  const series: PlotDatum[] = React.useMemo(() => {
    if (!data) {
      return [];
    }
    const filled = fillMissingBuckets(data.records);
    return filled.map((b) => ({
      start_time: b.start_time,
      end_time: b.end_time,
      value: "value" in b ? (b as { value: number }).value : undefined,
      label: nsToDateLabel(b.start_time),
    }));
  }, [data]);

  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const chartRef = React.useRef<Chart | null>(null);

  // Keep the click handler and series reachable from the (stable) chart
  // instance without recreating it whenever the parent re-renders.
  const clickRef = React.useRef(onBucketClick);
  clickRef.current = onBucketClick;
  const seriesRef = React.useRef(series);
  seriesRef.current = series;

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || series.length === 0) {
      return;
    }

    const color =
      getComputedStyle(canvas).getPropertyValue("--primary-main").trim() ||
      FALLBACK_COLOR;

    const chart = new Chart(canvas, {
      type: display === "bar" ? "bar" : "line",
      data: {
        labels: series.map((d) => d.label),
        datasets: [
          {
            label: metricName,
            data: series.map((d) => d.value ?? null),
            borderColor: color,
            backgroundColor: color,
            // Connect across missing buckets (rendered as null) instead of
            // leaving gaps; fillMissingBuckets still keeps the x-axis spacing
            // time-proportional.
            spanGaps: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: { mode: "index", intersect: false },
        onClick: (_event: ChartEvent, elements: ActiveElement[]) => {
          const index = elements[0]?.index;
          if (index === undefined) {
            return;
          }
          const datum = seriesRef.current[index];
          if (datum && datum.value !== undefined) {
            clickRef.current?.({
              start_time: datum.start_time,
              end_time: datum.end_time,
            });
          }
        },
        scales: {
          x: { ticks: { autoSkip: true, maxRotation: 0 } },
          y: { beginAtZero: true },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              title: (items) => {
                const index = items[0]?.dataIndex;
                if (index === undefined) {
                  return "";
                }
                const datum = series[index];
                return datum
                  ? `${nsToDateLabel(datum.start_time)} - ${nsToDateLabel(datum.end_time)}`
                  : "";
              },
              label: (item) => {
                const y = item.parsed.y;
                return typeof y === "number" && !Number.isNaN(y)
                  ? String(y)
                  : "—";
              },
            },
          },
        },
      },
    });
    chartRef.current = chart;

    return () => {
      chart.destroy();
      chartRef.current = null;
    };
  }, [series, display, metricName]);

  if (isLoading) {
    return (
      <div className={styles.state}>
        <CircularProgress size={24} />
      </div>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load metric data: {error.message}
      </Alert>
    );
  }

  if (series.length === 0) {
    return (
      <p className={styles.empty}>No data points in the selected window.</p>
    );
  }

  return (
    <div
      className={`${styles.container} ${onBucketClick ? styles.clickable : ""}`}
    >
      <canvas ref={canvasRef} role="img" aria-label={`${metricName} chart`} />
    </div>
  );
}
